import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMutation } from "@tanstack/react-query";
import { Outlet } from "react-router";
import styleSheet from "./style.module.scss";
import { starProps } from "../../orbit-interface";

// Store & Services
import { getOrbitDetails } from "../../../../feature/orbit-slice";
import { setBacisImages } from "../../../../feature/image-slice";
import { setFetchLoading } from "../../../../feature/loading-slice";
import authService from "../../../auth-module/services/auth-service";

// Utils
import { getCookie } from "../../../../utils/cookie";
import { getSessionData, setSessionData } from "../../../../utils/sessionstorage";
import { getLocalData, setLocalData } from "../../../../utils/localstorage";
import { useGetOrbitDetails } from "../../../../utils/orbitdetails";

// Components
import LoadingScreen from "../loading-screen";
import AppHeader from "./app-header";
import NotificationScreen from "../notification-screen";
import { getAuthDetails } from "../../../../feature/auth-slice";
import { getNotificationDetails, setNotification } from "../../../../feature/notification-slice";
import { useNotifyApi } from "../../../../utils/notifyHook";
import socket from "../../../../utils/socket";
import FilesCommentsManage from "../../components/files-comments-manage";
import Comments from "../../components/comments";

const AppScreen = () => {
  const dispatch = useDispatch();
  const { getNotify } = useNotifyApi();

  // Store data
  const orbitInfo = useSelector(getOrbitDetails);
  const authSlice = useSelector(getAuthDetails);
  const notificationSlice = useSelector(getNotificationDetails);
  const userId = authSlice.user_details?.usr_id;

  // Refs for latest auth state
  const authRef = useRef(authSlice);
  useEffect(() => {
    authRef.current = authSlice;
  }, [authSlice]);

  // Cookies & Storage
  const authToken = getCookie("auth_token");
  const language = getLocalData("tr");
  const showLoadingScreen = getSessionData("loading");

  // State
  const [isReady, setIsReady] = useState(false);
  const shouldShowLoading = showLoadingScreen === "1";
  const STAR_COUNT = 200;

  const stars = useMemo<starProps[]>(
    () =>
      Array.from({ length: STAR_COUNT }).map(() => ({
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 1 + 0.5,
        duration: Math.random() * 4 + 3,
        delay: Math.random() * 6,
      })),
    []
  );

  // API hooks
  const { getDetail, isPending: detailLoading } = useGetOrbitDetails();

  const { mutate: getImages, isPending: imageLoading } = useMutation({
    mutationFn: authService.getImages,
    onSuccess: (data) => {
      dispatch(setBacisImages(data.data.data));
      setIsReady(true);
    },
  });

  const onClickClose = useCallback(() => {
    dispatch(setNotification(false));
  }, [dispatch]);

  // Fetch orbit details on auth token
  useEffect(() => {
    if (authToken) getDetail();
  }, [authToken, getDetail]);

  // Fetch notifications when user ID is available
  useEffect(() => {
    if (userId) getNotify(userId);
  }, [userId, getNotify]);

  // Set loading state
  useEffect(() => {
    dispatch(setFetchLoading(detailLoading || imageLoading));
  }, [detailLoading, imageLoading, dispatch]);

  // Request Notification Permission on first user interaction
  useEffect(() => {
    const handleClick = () => {
      if (Notification.permission !== "granted") {
        Notification.requestPermission().then((permission) => {
          console.log("Notification permission:", permission);
        });
      }
    };
    window.addEventListener("click", handleClick, { once: true });
    return () => window.removeEventListener("click", handleClick);
  }, []);

  const onChangeSocket = useCallback(() => {
    const handler = (data: any) => {
      const currentUser = authRef.current.user_details?.usr_id;
      if (!currentUser) return;

      if (currentUser === data.noti_usr_id) {
        getNotify(currentUser);

        let noticeDetails;
        try {
          noticeDetails = typeof data.notice === "string" ? JSON.parse(data.notice) : data.notice;
        } catch {
          return;
        }

        if (Notification.permission === "granted") {
          new Notification("New Notification", {
            body: noticeDetails.noticeText || "You have a new notification.",
            icon: "",
            requireInteraction: true,
          });
        }
      }
    };

    socket.on("notification", handler);

    return () => {
      socket.off("notification", handler);
    };
  }, [getNotify]);

  useEffect(() => {
    if (showLoadingScreen === null) setSessionData("loading", "1");
    getImages();
    if (!language) setLocalData("tr", "de");

    const cleanup = onChangeSocket();
    return cleanup;
  }, [getImages, language, onChangeSocket, showLoadingScreen]);

  if (!isReady) return null;

  return (
  
    <div className={styleSheet.app} onClick={onClickClose}>
      {stars.map((star, i) => (
        <div
          key={i}
          className={styleSheet.star}
          style={{
            width: `${star.size}px`,
            height: `${star.size}px`,
            top: `${star.y}%`,
            left: `${star.x}%`,
            animationDuration: `${star.duration}s`,
            animationDelay: `${star.delay}s`,
          }}
        />
      ))}
      {shouldShowLoading && <LoadingScreen />}
      {orbitInfo.header && <AppHeader />}
      {!detailLoading && <Outlet />}

      <NotificationScreen open={notificationSlice.notification} />

                        
    </div>
  );
};

export default AppScreen;
